import { useState, useEffect } from 'react';

import { CustomerAddress } from '../types';
import { getLocalTimeInfo } from '../utils/timezone';

export interface CustomerLocalTimeInfo {
  time: string | null;
  timezone: string | null;
  abbreviation: string | null;
}

/**
 * Hook to get and update customer's local time based on their address
 * Updates every minute to keep time current
 *
 * @param address - Customer address with zip_code
 * @returns Object with current local time, timezone, and abbreviation
 */
export const useCustomerLocalTime = (address: CustomerAddress | null): CustomerLocalTimeInfo => {
  const [timeInfo, setTimeInfo] = useState<CustomerLocalTimeInfo>({
    time: null,
    timezone: null,
    abbreviation: null,
  });

  useEffect(() => {
    if (!address?.zip_code) {
      setTimeInfo({
        time: null,
        timezone: null,
        abbreviation: null,
      });
      return;
    }

    // Update time immediately
    const updateTime = () => {
      const info = getLocalTimeInfo(address.zip_code);
      if (info) {
        setTimeInfo(info);
      } else {
        setTimeInfo({
          time: null,
          timezone: null,
          abbreviation: null,
        });
      }
    };

    updateTime();

    // Update time every minute
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, [address?.zip_code]);

  return timeInfo;
};
